import { useLazyQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SEARCH_QUERY } from '../../graphql/resolvers/searchQueries/search.gql';
import './search.scss';

type Tab = 'all' | 'stories' | 'authors' | 'replies';

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryParam = searchParams.get('q') || '';
  const [activeTab, setActiveTab] = useState<Tab>('all');

  const [executeSearch, { data, loading }] = useLazyQuery(SEARCH_QUERY, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (queryParam) {
      executeSearch({ variables: { query: queryParam, limit: 20 } });
    }
  }, [queryParam, executeSearch]);

  const results = data?.search;
  const posts = results?.posts || [];
  const authors = results?.authors || [];
  const replies = results?.replies || [];

  function handlePostClick(post: any) {
    const postId = post.PK?.replace('POST#', '') || '';
    navigate(`/post/${postId}`);
  }

  function handleAuthorClick(author: any) {
    navigate(`/user/${author.username}/profile`);
  }

  function handleReplyClick(reply: any) {
    const parentPostId = reply.parentPostId || reply.rootPostId || '';
    navigate(`/post/${parentPostId}`);
  }

  function formatDate(dateStr: string) {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  const showStories = activeTab === 'all' || activeTab === 'stories';
  const showAuthors = activeTab === 'all' || activeTab === 'authors';
  const showReplies = activeTab === 'all' || activeTab === 'replies';

  const totalResults =
    (results?.totalPosts ?? 0) +
    (results?.totalAuthors ?? 0) +
    (results?.totalReplies ?? 0);

  return (
    <div className="Search__Page">
      <div className="Search__Page__Header">
        <h1 className="Search__Page__Title">Search Results</h1>
        {queryParam && (
          <p className="Search__Page__Query">
            {results
              ? `${results.totalPosts} stories, ${results.totalAuthors} authors, ${results.totalReplies} replies`
              : 'Searching...'}{' '}
            for &ldquo;{queryParam}&rdquo;
          </p>
        )}
      </div>

      <div className="Search__Page__Tabs">
        {(['all', 'stories', 'authors', 'replies'] as Tab[]).map((tab) => (
          <button
            key={tab}
            className={`Search__Page__Tab ${activeTab === tab ? 'Search__Page__Tab--active' : ''}`}
            onClick={() => setActiveTab(tab)}
            type="button"
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {loading && <div className="Search__Page__Loading">Searching...</div>}

      {!loading && !queryParam && (
        <div className="Search__Page__Empty">
          Enter a search query to find stories, authors, and replies.
        </div>
      )}

      {!loading && queryParam && totalResults === 0 && (
        <div className="Search__Page__Empty">
          No results found for &ldquo;{queryParam}&rdquo;
        </div>
      )}

      {showStories && posts.length > 0 && (
        <>
          {posts.map((post: any) => (
            <div
              key={`${post.PK}#${post.SK}`}
              className="Search__Result__Card"
              onClick={() => handlePostClick(post)}
            >
              <h3 className="Search__Result__Card__Title">{post.title}</h3>
              {post.subtitle && (
                <p className="Search__Result__Card__Subtitle">
                  {post.subtitle}
                </p>
              )}
              <div className="Search__Result__Card__Meta">
                <span>@{post.username}</span>
                {post.category && (
                  <span className="Search__Result__Card__Category">
                    {post.category}
                  </span>
                )}
                {post.readTime && <span>{post.readTime} min read</span>}
                {post.createdAt && <span>{formatDate(post.createdAt)}</span>}
              </div>
            </div>
          ))}
        </>
      )}

      {showAuthors && authors.length > 0 && (
        <>
          {authors.map((author: any) => (
            <div
              key={`${author.PK}#${author.SK}`}
              className="Search__Author__Card"
              onClick={() => handleAuthorClick(author)}
            >
              <div className="Search__Author__Card__Username">
                @{author.username}
              </div>
              {author.displayName && (
                <div className="Search__Author__Card__DisplayName">
                  {author.displayName}
                </div>
              )}
              {author.bio && (
                <div className="Search__Author__Card__Bio">{author.bio}</div>
              )}
            </div>
          ))}
        </>
      )}

      {showReplies && replies.length > 0 && (
        <>
          {replies.map((reply: any) => (
            <div
              key={`${reply.PK}#${reply.SK}`}
              className="Search__Reply__Card"
              onClick={() => handleReplyClick(reply)}
            >
              <div className="Search__Reply__Card__Badge">Reply</div>
              <h3 className="Search__Reply__Card__Title">
                {reply.title || 'Untitled reply'}
              </h3>
              <div className="Search__Reply__Card__Meta">
                <span>@{reply.username}</span>
                {reply.createdAt && <span>{formatDate(reply.createdAt)}</span>}
                <span className="Search__Reply__Card__Link">
                  View in thread &rarr;
                </span>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
